import { PDA,TransactionBuilder } from '@orca-so/common-sdk'
import {
  TICK_ARRAY_SIZE,
  TickArrayUtil,
  TickUtil,
  Whirlpool,
  WhirlpoolClient,
  WhirlpoolContext,
} from '@renec-foundation/nemoswap-sdk'
import { initTickArrayIx } from '@renec-foundation/nemoswap-sdk/dist/instructions'
import { NATIVE_MINT } from '@solana/spl-token'
import { PublicKey } from '@solana/web3.js'

const TX_SIZE_LIMIT = 1232
const ADDITIONAL_COST_OF_NOT_INIT_TICK_AT_EDGE = 32
const BYTES_PER_TICK_ARRAY_IX = 51

type TickArrayIxCapacity = {
  numOfTickArrays: number;
  txSize: number;
}

const TOKEN_AND_TOKEN: TickArrayIxCapacity = {
  numOfTickArrays: 10,
  txSize: 1208,
}
const TOKEN_AND_RENEC: TickArrayIxCapacity = {
  numOfTickArrays: 7,
  txSize: 1223,
}

export const getInitTickArrayTx = async (
  client: WhirlpoolClient,
  whirlpool: Whirlpool,
  tickLowerIndex: number,
  tickUpperIndex: number,
  funder?: PublicKey,
) => {
  const { tickSpacing } = whirlpool.getData()

  const allSurroundingTicksArray = getAllSurroundingTicksArrayInRange(
    tickLowerIndex,
    tickUpperIndex,
    tickSpacing,
  )

  const edgesUninitializedTickArrays =
    await TickArrayUtil.getUninitializedArraysPDAs(
      [tickLowerIndex, tickUpperIndex],
      client.getContext().program.programId,
      whirlpool.getAddress(),
      tickSpacing,
      client.getFetcher(),
      true,
    )

  const totalInitTickArraysIxs = calculateTotalNumberOfInitTickArrayIxs(
    whirlpool,
    2 - edgesUninitializedTickArrays.length,
  )

  // pick closet uninitialized tick array to the current tick due to tx size limit
  const surroundingUninitializedTickArrays =
    await getClosestUninitializedTickArray(
      client,
      whirlpool,
      allSurroundingTicksArray,
      tickSpacing,
      totalInitTickArraysIxs - edgesUninitializedTickArrays.length,
    )

  // Construct the init tick array tx
  const initTickTx = constructTheInitTickArrayTx(
    client.getContext(),
    whirlpool.getAddress(),
    surroundingUninitializedTickArrays,
    edgesUninitializedTickArrays,
    funder,
  )

  return initTickTx
}

export const getAllSurroundingTicksArrayInRange = (
  tickLower: number,
  tickUpper: number,
  tickSpacing: number,
): number[] => {
  const startTickLower = TickUtil.getStartTickIndex(tickLower, tickSpacing)
  const startTickUpper = TickUtil.getStartTickIndex(tickUpper, tickSpacing)

  // Get all start ticks in range
  const startTicks: number[] = []
  const increment = TICK_ARRAY_SIZE * tickSpacing
  // start tick lower and upper are exclusive
  for (let i = startTickLower + increment; i < startTickUpper; i += increment) {
    startTicks.push(i)
  }
  return startTicks
}

export const getClosestUninitializedTickArray = async (
  client: WhirlpoolClient,
  whirlpool: Whirlpool,
  allSurroundingTicksArray: number[],
  tickSpacing: number,
  maxIxs: number,
): Promise<
  {
    startIndex: number;
    pda: PDA;
  }[]
> => {
  // Get all uninitialized ticks
  const initTickArrayStartPDAs = await TickArrayUtil.getUninitializedArraysPDAs(
    allSurroundingTicksArray,
    client.getContext().program.programId,
    whirlpool.getAddress(),
    tickSpacing,
    client.getFetcher(),
    true,
  )

  if (initTickArrayStartPDAs.length <= maxIxs) {
    return initTickArrayStartPDAs
  }

  // sort ascending by start index
  initTickArrayStartPDAs.sort((a, b) => a.startIndex - b.startIndex)
  const currentTickIndex = whirlpool.getData().tickCurrentIndex

  // Get the start tick index that passes the current tick
  let index = -1
  for (let i = 0; i < initTickArrayStartPDAs.length; i++) {
    if (currentTickIndex < initTickArrayStartPDAs[i].startIndex) {
      index = i
      break
    } else if (currentTickIndex === initTickArrayStartPDAs[i].startIndex) {
      index = i + 1 // index of the next tick array
      break
    }
  }

  if (index === -1 || index >= initTickArrayStartPDAs.length) {
    return [] // no tick arrays need to be initialized
  }

  // Distribute uninitializedIxs surrounding the current tick. Take left first
  let availableIxs = maxIxs
  let maxLeft = index - 1
  let maxRight = index
  let pickFromLeft = true

  const resultIndices: number[] = []
  while (
    availableIxs > 0 &&
    (maxLeft >= 0 || maxRight < initTickArrayStartPDAs.length)
  ) {
    if (pickFromLeft && availableIxs > 0 && maxLeft >= 0) {
      resultIndices.push(maxLeft)
      maxLeft--
      availableIxs--
    } else if (
      !pickFromLeft &&
      availableIxs > 0 &&
      maxRight < initTickArrayStartPDAs.length
    ) {
      resultIndices.push(maxRight)
      maxRight++
      availableIxs--
    }

    pickFromLeft = !pickFromLeft
  }
  const selectedPDAs = resultIndices.map(
    (index) => initTickArrayStartPDAs[index],
  )

  return selectedPDAs
}

const constructTheInitTickArrayTx = (
  context: WhirlpoolContext,
  whirlpoolPubkey: PublicKey,
  surroundingUninitializedTickArrays: {
    startIndex: number;
    pda: PDA;
  }[],
  edgesUninitializedTickArrays: {
    startIndex: number;
    pda: PDA;
  }[],
  funder?: PublicKey,
): TransactionBuilder => {
  const provider = context.provider
  const program = context.program
  const txBuilder = new TransactionBuilder(
    provider.connection,
    provider.wallet,
  )

  surroundingUninitializedTickArrays.forEach((initTickArrayInfo) => {
    txBuilder.addInstruction(
      initTickArrayIx(program, {
        startTick: initTickArrayInfo.startIndex,
        tickArrayPda: initTickArrayInfo.pda,
        whirlpool: whirlpoolPubkey,
        funder: !!funder ? funder : provider.wallet.publicKey,
      }),
    )
  })

  edgesUninitializedTickArrays.forEach((initTickArrayInfo) => {
    txBuilder.addInstruction(
      initTickArrayIx(program, {
        startTick: initTickArrayInfo.startIndex,
        tickArrayPda: initTickArrayInfo.pda,
        whirlpool: whirlpoolPubkey,
        funder: !!funder ? funder : provider.wallet.publicKey,
      }),
    )
  })

  return txBuilder
}

// Do this, since the simulate to calculation the tx size is slow
export const calculateTotalNumberOfInitTickArrayIxs = (
  whirlpool: Whirlpool,
  numOfEdgesArrayInitialized: number,
) => {
  if (numOfEdgesArrayInitialized < 0) {
    throw new Error('numOfEdgesArrayInitialized cannot be negative')
  }

  let tickArrayIxCapacity: TickArrayIxCapacity
  if (isWhirlpoolContainRenec(whirlpool)) {
    tickArrayIxCapacity = TOKEN_AND_RENEC
  } else {
    tickArrayIxCapacity = TOKEN_AND_TOKEN
  }

  // For one edge initialized, cost more 32 bytes. For a numOfTickArrays left, cost less 51 bytes
  tickArrayIxCapacity.txSize = tickArrayIxCapacity.txSize + ADDITIONAL_COST_OF_NOT_INIT_TICK_AT_EDGE * numOfEdgesArrayInitialized
  while (tickArrayIxCapacity.txSize > TX_SIZE_LIMIT) {
    tickArrayIxCapacity.numOfTickArrays--
    tickArrayIxCapacity.txSize = tickArrayIxCapacity.txSize - BYTES_PER_TICK_ARRAY_IX
  }

  return tickArrayIxCapacity.numOfTickArrays
}

const isWhirlpoolContainRenec = (whirlpool: Whirlpool): boolean => {
  const tokenMintA = whirlpool.getData().tokenMintA
  const tokenMintB = whirlpool.getData().tokenMintB
  return tokenMintA.equals(NATIVE_MINT) || tokenMintB.equals(NATIVE_MINT)
}
